import UnicornTopbar from "@/components/unicorn-tracker/topbar";
import React, { useState, useEffect, useCallback } from "react";
import dynamic from "next/dynamic";
import { useAtom } from "jotai";
import { loginPopup } from "@/Atoms";
import SignUpUnicornTracker from "@/components/modal/SignUpUnicornTracker";
import useAuth from "@/hooks/auth";
import { useRouter } from "next/router";
import {
  useRouter as useNavRouter,
  useSearchParams,
  usePathname,
} from "next/navigation";
import { useIsMobile } from "@/hooks/useIsMobile";
import MobileResizableDrawer from "@/components/unicorn-tracker/mobile-resizable-drawer";

import UnicornListsMobile from "@/components/unicorn-tracker/unicorn-lists-mobile";
import { getUnicornRegions } from "@/services/api/UnicornTracker";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/components/ui/resizable";

// Create loading component for better UX
const LoadingComponent = ({ text = "Loading..." }) => (
  <div className='flex items-center justify-center h-full rounded-lg bg-gray-50'>
    <div className='text-center'>
      <div className='w-8 h-8 mx-auto mb-2 border-b-2 border-gray-900 rounded-full animate-spin'></div>
      <p className='text-sm text-gray-600'>{text}</p>
    </div>
  </div>
);

// Dynamically import components with loading states for better performance
const UnicornMaps = dynamic(() => import("@/components/unicorn-tracker/maps"), {
  ssr: false,
  loading: () => <LoadingComponent text='Loading map...' />,
});

const UnicornSidebar = dynamic(
  () => import("@/components/unicorn-tracker/sidebar"),
  {
    loading: () => <LoadingComponent text='Loading sidebar...' />,
  },
);

const UnicornLists = dynamic(
  () => import("@/components/unicorn-tracker/unicorn-lists"),
  {
    loading: () => <LoadingComponent text='Loading unicorn list...' />,
  },
);

export default function UnicornTracker() {
  const router = useRouter();
  const isMobile = useIsMobile();
  let [, setIsLoginPopup] = useAtom(loginPopup);
  const { user, isLoading } = useAuth({
    middleware: "guest",
    redirectIfAuthenticated: false,
  });

  // ALL STATE HOOKS AT THE TOP
  const [authChecked, setAuthChecked] = useState(false);
  const [filters, setFilters] = useState({
    regions: [],
    countries: [],
    sectors: [],
    cities: [],
  });

  // State for mobile drawer data
  const [mobileDrawerData, setMobileDrawerData] = useState({
    // Sidebar data
    regionData: null,
    countryData: null,
    selectedCountryName: null,
    selectedRegionName: null,
    unicornStats: null,
    dataLoading: false,
    specialReports: [],
    spotlights: [],
    podcasts: [],
    otherContent: [],
    exploreTooltip: "",
    // List data
    unicorns: [],
    loading: true,
    loadingMore: false,
    hasMore: false,
    statusFilter: { current: true, fallen: true, alumni: true },
    statusCounts: { current: 0, fallen: 0, alumni: 0 },
    searchQuery: "",
    currentPage: 1,
  });

  // ALL CALLBACKS BEFORE ANY RETURNS
  const handleLoadMore = useCallback(() => {
    setMobileDrawerData((prev) => ({
      ...prev,
      currentPage: prev.currentPage + 1,
    }));
  }, []);

  // This callback will receive data from the SINGLE UnicornSidebar component
  const handleSidebarDataUpdate = useCallback((data) => {
    setMobileDrawerData((prev) => ({
      ...prev,
      ...data,
    }));
  }, []);

  const handleListDataUpdate = useCallback((data) => {
    setMobileDrawerData((prev) => ({
      ...prev,
      ...data,
    }));
  }, []);

  const handleSearchChange = useCallback((query) => {
    setMobileDrawerData((prev) => ({
      ...prev,
      searchQuery: query,
      currentPage: 1,
      loading: true,
      unicorns: [],
    }));
  }, []);

  const navRouter = useNavRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  // ALL useEffect HOOKS BEFORE ANY RETURNS
  useEffect(() => {
    if (router.isReady && searchParams.toString() === "") {
      navRouter.replace(`${pathname}?unicorn_regions=1`);
    }
  }, [router.isReady, pathname, searchParams, navRouter]);

  useEffect(() => {
    if (!isLoading) {
      setAuthChecked(true);
      if (!user) {
        const timer = setTimeout(() => {
          setIsLoginPopup(true);
        }, 100);
        return () => clearTimeout(timer);
      } else {
        setIsLoginPopup(false);
      }
    }
  }, [user, isLoading, setIsLoginPopup]);

  // NOW CONDITIONAL RETURN
  if (!authChecked) {
    return (
      <div className='flex items-center justify-center h-screen bg-gray-900'>
        <div className='text-center'>
          <div className='w-12 h-12 mx-auto mb-4 border-b-2 border-white rounded-full animate-spin'></div>
          <p className='text-white'>Loading Unicorn Tracker...</p>
        </div>
      </div>
    );
  }

  // Helper functions
  const formatCurrency = (value) => {
    if (!value || value === 0) return "Undisclosed";
    const billions = value / 1000000000;
    if (billions >= 1) {
      return `$${billions.toFixed(1)}B`;
    }
    const millions = value / 1000000;
    return `$${millions.toFixed(0)}M`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return dateString;
    }
  };

  const formatTimeToUnicornYears = (startup) => {
    if (startup?.time_to_unicorn_display) {
      return startup.time_to_unicorn_display;
    }
    const raw = startup?.time_to_unicorn;
    const num = Number(raw);
    if (!Number.isFinite(num) || num <= 0) return "N/A";
    return `${num.toFixed(1)} years`;
  };

  const getUnicornStatus = (startup) => {
    const isTruthy = (val) =>
      val === true || val === "true" || val === 1 || val === "1";
    if (isTruthy(startup.is_alumni)) return "Alumni";
    if (isTruthy(startup.is_lost_unicorn)) return "Fallen";
    if (isTruthy(startup.is_decacorn)) return "Decacorn";
    return "Unicorn";
  };

  const getLatestStage = (startup) => {
    if (startup.has_ipo_data) return "IPO";
    if (startup.has_acquisition_data) return "Acquired";
    return startup.current_stage?.name || "Private";
  };

  return (
    <>
      <SignUpUnicornTracker />
      <div className='flex flex-col h-dvh'>
        <UnicornTopbar filters={filters} setFilters={setFilters} />

        {/* Conditional render: only mount the view matching the current device.
            Previously both views were always mounted (sm:hidden / hidden sm:flex)
            causing 2x API calls and 2x map renders on every page load. */}
        {isMobile ? (
          <div className='flex flex-col flex-1 h-[calc(100dvh-64px)] relative'>
            <UnicornMaps filters={filters} />

            {/* Hidden sidebar + list that share data with mobile drawer via callbacks */}
            <div style={{ display: "none" }}>
              <UnicornSidebar
                filters={filters}
                onDataUpdate={handleSidebarDataUpdate}
              />
              <UnicornListsMobile
                filters={filters}
                showLostUnicornOrAlumni={
                  mobileDrawerData.showLostUnicornOrAlumni
                }
                currentPage={mobileDrawerData.currentPage}
                onDataUpdate={handleListDataUpdate}
              />
            </div>

            <MobileResizableDrawer
              regionData={mobileDrawerData.regionData}
              countryData={mobileDrawerData.countryData}
              selectedCountryName={mobileDrawerData.selectedCountryName}
              selectedRegionName={mobileDrawerData.selectedRegionName}
              unicornStats={mobileDrawerData.unicornStats}
              dataLoading={mobileDrawerData.dataLoading}
              specialReports={mobileDrawerData.specialReports}
              spotlights={mobileDrawerData.spotlights}
              podcasts={mobileDrawerData.podcasts}
              otherContent={mobileDrawerData.otherContent}
              exploreTooltip={mobileDrawerData.exploreTooltip}
              unicorns={mobileDrawerData.unicorns}
              loading={mobileDrawerData.loading}
              loadingMore={mobileDrawerData.loadingMore}
              hasMore={mobileDrawerData.hasMore}
              onLoadMore={handleLoadMore}
              formatCurrency={formatCurrency}
              formatDate={formatDate}
              formatTimeToUnicornYears={formatTimeToUnicornYears}
              getUnicornStatus={getUnicornStatus}
              getLatestStage={getLatestStage}
              statusFilter={mobileDrawerData.statusFilter}
              statusCounts={mobileDrawerData.statusCounts}
              setStatusFilter={(filter) =>
                setMobileDrawerData((prev) => ({
                  ...prev,
                  statusFilter: filter,
                  currentPage: 1,
                  loading: true,
                  unicorns: [],
                }))
              }
              onSearchChange={handleSearchChange}
            />
          </div>
        ) : (
          <div className='flex w-full h-[calc(100dvh-64px)] gap-3 p-3 mt-16'>
            <div className='w-[calc(100vw-396px)]'>
              <ResizablePanelGroup direction='vertical'>
                <ResizablePanel>
                  <UnicornMaps filters={filters} />
                </ResizablePanel>
                <ResizableHandle withHandle />
                <ResizablePanel defaultSize={30} minSize={30}>
                  <UnicornLists filters={filters} />
                </ResizablePanel>
              </ResizablePanelGroup>
            </div>
            <UnicornSidebar
              filters={filters}
              onDataUpdate={handleSidebarDataUpdate}
            />
            <UnicornListsMobile
              filters={filters}
              statusFilter={mobileDrawerData.statusFilter}
              searchQuery={mobileDrawerData.searchQuery}
              currentPage={mobileDrawerData.currentPage}
              onDataUpdate={handleListDataUpdate}
            />
          </div>
        )}
      </div>
    </>
  );
}
